/**
 * @file JS - Controller - VectorLayerController.js
 * @author : Adam Lemire
 * @since 2019/11/18
 * @version 1.1
 * @summary Dernières modifications : 2021/12/06
 */

import iro from '@jaames/iro';
import { gisSettings } from '../../gisSettings.js';
import LayerController from './LayerController';
import VectorLayerAdapter from '../../adapters/VectorLayerAdapter.js';
import VectorLayer from '../../classes/Layer/VectorLayer.js'; // eslint-disable-line no-unused-vars
import VectorLayerStyle from '../../classes/Layer/VectorLayerStyle.js';
import { GisSection, LayerProperties } from '../../helpers/Selectors.js';
import { ClassificationType, ColorGradient, ColorHexList, FeatureType, Luminosity, StyleTypes, TypeAttribut } from '../../helpers/Constants.js'; // eslint-disable-line no-unused-vars
import { dictionary } from '../../helpers/languageDictionary.js';
import '../../helpers/typedef.js';
import { Evenements } from '../../events/Evenements.js';

const OPTION_PROPRIETES_COUCHE = gisSettings.options.layer.editProperties.active;
const OPTION_LIBELLÉ = gisSettings.options.layer.coucheLabel.active;

const CLASSE_STYLE_AUTRE = 'style-autres-inclus';
const CLASSE_LAYER_SANS_STYLE = 'no-layer-style';
const CLASSE_TYPE_GRADUÉ = '.type-graduated';
const CLASSE_TYPE_CATÉGORISÉ = '.type-categories';
const CLASSE_INFO_CLASSIFICATION = '.info-classification';

/**
 * @classdesc sous-controller de LayerController - Géré par LayerAdapter
 * @augments LayerController
 */
export default class VectorLayerController extends LayerController {
  constructor () {
    super();
    /** @type { VectorLayer } */
    this._layer = undefined;
    /** @type { iro.ColorPicker } */
    this._colorPicker = OPTION_PROPRIETES_COUCHE
      ? new iro.ColorPicker(LayerProperties.COLOR_PICKER.CONTENT, {
        width: 200,
        transparency: true
      })
      : undefined;
    /** @type { VectorLayerAdapter } */
    this._vectorLayerAdapter = undefined;
    this._vectorLayerStyle = new VectorLayerStyle({});
    this._observateurSectionStyleSymbole = ecouterAfficherSymbolStyle();
  }

  get layer () {
    return this._layer;
  }

  /** @override */
  set layer (layer) {
    const _this = this;
    this._layer = layer;
    this._vectorLayerStyle = Object.assign(Object.create(Object.getPrototypeOf(_this._layer.styleOptions)), _this._layer.styleOptions);
    this._ecouterFeatureTypePret(layer);
  }

  /** @param { VectorLayer } layer */
  static créerSymboleLégende (layer) {
    const parentDivName = '.map-layers__legend-' + layer.id;
    const parentDiv = $(parentDivName);
    const legendSymbolDiv = VectorLayerAdapter.getLegendSymbolDiv(layer.styleOptions, layer.featureType);

    parentDiv.empty();
    parentDiv.append(legendSymbolDiv);
    $(GisSection.MAP_LAYERS_LAYER).resize();
  }

  /*
   * OVERRIDE - PROPRIÉTÉS DE LA COUCHE ------------------------------------------------------------------
   */

  /** @override */
  _updateLayerPropertiesListeners () {
    const _this = this;
    $(LayerProperties.FILL.COLOR).off('click tap').on('click tap', () => _this._showFillColorPicker());
    $(LayerProperties.BUTTON.STROKE_COLOR).off('click tap').on('click tap', () => _this._showStrokeColorPicker());
    $(LayerProperties.LAYER_STROKE_WIDTH).off('change').on('change', () => _this._actualiserSymbolesFenetreProprietes());
    $(LayerProperties.LAYER_SYMBOL_SIZE).off('change').on('change', () => _this._actualiserSymbolesFenetreProprietes());
    $(LayerProperties.SYMBOL_STYLE_TYPE).off('change').on('change', () => _this._afficherProprietesStyleSpecifiqueTypeSymbole(true));
    $(LayerProperties.GROUP_CLASSIFICATION_SELECTOR).off('change').on('change', () => _this._actualiserProprietesGroupesDepuisChangementClassification());
    $(LayerProperties.GROUP_VALUE_SELECTOR).off('change').on('change', () => _this._afficherSectionGroupes(true));
    $(LayerProperties.GRADATION_TYPE_INPUT).off('change').on('change', () => _this._changerTypeGradation());
    $(LayerProperties.PRECISION_SELECTOR).off('change').on('change', () => _this._actualiserProprietesGroupesDepuisChangementClassification());
    $(LayerProperties.GROUP_CLASSIFICATION_QUANTITY_SELECTOR).off('change').on('change', () => _this._actualiserProprietesGroupesDepuisChangementClassification());
    $(LayerProperties.COLOR_GRADIENT_SELECTOR).off('change').on('change', (event) => _this._changerGradientCouleur($(event.currentTarget).val()));
    $(LayerProperties.COLOR_THEME_SELECTOR).off('change').on('change', () => _this._changerLuminosité($(LayerProperties.COLOR_THEME_SELECTOR).val()));
    $(LayerProperties.BUTTON.APPLY).off('click tap').on('click tap', () => _this._modifierPropriétés());
    $(LayerProperties.COLOR_PICKER.CANCEL_BUTTON).off('click tap').on('click tap', () => $(LayerProperties.COLOR_PICKER.CONTAINER).toggleClass('hidden'));
    $(LayerProperties.INFO_CLASSIFICATION_BOUTON).off('click tap').on('click tap', () => afficherInfoClassification());
    $(LayerProperties.INFO_CLASSIFICATION_FERMER).off('click tap').on('click tap', () => fermerInfoClassification());
    $(LayerProperties.SYMBOLE_AUTRES_COULEUR).off('click tap').on('click tap', () => _this._afficherSelecteurCouleurRemplissageAutres());
    $(LayerProperties.SYMBOLE_AUTRES_CONTOUR_COULEUR).off('click tap').on('click tap', () => _this._afficherSelecteurCouleurContourAutres());
    $(LayerProperties.SYMBOLE_AUTRES_CONTOUR_EPAISSEUR).off('change').on('change', (event) => _this._actualiserSymbolesFenetreProprietes());
    $(LayerProperties.SYMBOLE_AUTRES_TAILLE).off('change').on('change', (event) => _this._actualiserSymbolesFenetreProprietes());
  }

  /** @override */
  async _updateLayerPropertiesStyleView () {
    const layer = await this._layer;
    $(LayerProperties.SECTION).data('featureType', layer.featureType);
    $(LayerProperties.SECTION_RASTER).hide();
    $(LayerProperties.SECTION_VECTOR).show();
    $(LayerProperties.LAYER_SOURCE_VECTOR).show();

    if (layer.donneesImportees) {
      $(LayerProperties.LAYER_STYLE).show().parent().removeClass(CLASSE_LAYER_SANS_STYLE);
      $(LayerProperties.LAYER_SOURCE_AUTRES_CHECKBOX).off('change');
      $(LayerProperties.LAYER_SOURCE_AUTRES_CHECKBOX).prop('checked', layer.inclureDonneesNonImportees);
      $(LayerProperties.LAYER_SOURCE_AUTRES_CHECKBOX).on('change', function () {
        layer.inclureDonneesNonImportees = !layer.inclureDonneesNonImportees;
        $(LayerProperties.SECTION_VECTOR).toggleClass(CLASSE_STYLE_AUTRE);
      });
      layer.inclureDonneesNonImportees
        ? $(LayerProperties.SECTION_VECTOR).addClass(CLASSE_STYLE_AUTRE)
        : $(LayerProperties.SECTION_VECTOR).removeClass(CLASSE_STYLE_AUTRE);

      this._actualiserCheckboxConversionPoint(layer);
    } else {
      $(LayerProperties.LAYER_STYLE).hide().parent().addClass(CLASSE_LAYER_SANS_STYLE);
      $(LayerProperties.LAYER_SOURCE_AUTRES).hide();
      $(LayerProperties.LAYER_SOURCE_POINT).hide();
      $(LayerProperties.LAYER_SOURCE_POINT_CHECKBOX).prop('checked', false);
      $(LayerProperties.SECTION_VECTOR).removeClass(CLASSE_STYLE_AUTRE);
    }

    this._actualiserVueProprietesStyleVectorLayer();
    this._addVectorLayerColumnsToLayerProperties();
  }

  // ! en cours - si point et gradué par taille, reset à singleStyle. Doit aussi mettre a jour section gradué
  /** @param { VectorLayer } layer */
  _actualiserCheckboxConversionPoint (layer) {
    const _this = this;
    if (layer.featureType !== FeatureType.LINE) {
      $(LayerProperties.LAYER_SOURCE_POINT_CHECKBOX).off('change');
      $(LayerProperties.LAYER_SOURCE_POINT_CHECKBOX).prop('checked', layer.convertiEnPoint);
      $(LayerProperties.LAYER_SOURCE_POINT_CHECKBOX).on('change', async function () {
        const featureType = $(LayerProperties.LAYER_SOURCE_POINT_CHECKBOX).prop('checked') ? FeatureType.POINT : FeatureType.POLYGON;
        _this._vectorLayerAdapter.type = featureType;
        _this._vectorLayerAdapter.showFeatureStyleProperties(featureType);
        _this._vectorLayerAdapter.createSymbolPreview(featureType);
        $(LayerProperties.BUTTON.CANCEL).off('click tap').on('click tap', function () {
          $(LayerProperties.SECTION).addClass('hidden');
          _this._vectorLayerAdapter.type = featureType === FeatureType.POINT ? FeatureType.POLYGON : FeatureType.POINT;
        });
      });
    } else {
      $(LayerProperties.LAYER_SOURCE_POINT).hide();
      $(LayerProperties.LAYER_SOURCE_POINT_CHECKBOX).prop('checked', false);
    }
  }

  /*
   * override - APPLIQUER LES PROPRIÉTÉS DE LA COUCHE ----------------------------------------------------
   */

  /** @override */
  _modifierStyle () {
    this._gérerConversionOlLayer();
    this._appliquerNouveauStyleAutres();
    if ($(LayerProperties.SYMBOL_STYLE_TYPE).val() === StyleTypes.SINGLE) {
      this._appliquerNouveauStyleSymboleUnique();
    } else if (
      ($(LayerProperties.SYMBOL_STYLE_TYPE).val() === StyleTypes.CATEGORIZED || $(LayerProperties.SYMBOL_STYLE_TYPE).val() === StyleTypes.GRADUATED) &&
      $(LayerProperties.GROUP_VALUE_SELECTOR).val()) {
      this._appliquerNouveauStyleGroupes();
    }
  }

  /*
   * OVERRIDE - LEFT-MENU : MAP_LAYERS
   */

  /**
   * @override
   * @param { VectorLayer } vectorLayer
   * @returns { boolean }
   */
  async _créerDivCoucheDansListeCouches (vectorLayer) {
    const layer = vectorLayer;
    const avecLibellés = await vectorLayer.getLibellésExistent();
    const checked = layer.isVisible ? 'checked' : '';
    const divId = '#map-layers__layer-' + layer.id;
    const avecDonneesJointes = layer.joinField ? 'map-layers__layer--vector-data' : '';
    const ligneLibellés = avecLibellés
      ? '<input type="checkbox" class="map-layers__label-checkbox map-layers__label-checkbox-' + layer.id + '" ' + checked + '>'
      : '';

    $(GisSection.MAP_LAYERS_CONTAINER).prepend('<div id="map-layers__layer-' + layer.id + '" class="map-layers__layer map-layers__layer--vector ' + avecDonneesJointes + ' map-layers__layer-' + layer.id + '">' +
      '<div class="map-layers__text map-layers__text-' + layer.id + '">' + layer.name + '</div>' +
      '<input type="checkbox" class="map-layers__checkbox map-layers__checkbox-' + layer.id + '" ' + checked + '>' +
      ligneLibellés +
      '<div class="map-layers__legend map-layers__legend-' + layer.id + '"></div>' +
      '<div class="map-layers__move-icon"></div>' +
      '<div class="map-layers__separator"></div>' +
      '</div>');

    $(divId).data('layerId', layer.id);
    return true;
  }

  /*
   * spécifique - APPLIQUER LES PROPRIÉTÉS DE LA COUCHE ----------------------------------------------------
   */

  _gérerConversionOlLayer () {
    if ($(LayerProperties.LAYER_SOURCE_POINT_CHECKBOX).prop('checked') !== this._layer.convertiEnPoint) {
      this._layer.convertiEnPoint = !this._layer.convertiEnPoint;
    }
  }

  _appliquerNouveauStyleAutres () {
    this._vectorLayerStyle.styleAutres.fillColor = $(LayerProperties.SYMBOLE_AUTRES_COULEUR).css('background-color');
    this._vectorLayerStyle.styleAutres.strokeColor = $(LayerProperties.SYMBOLE_AUTRES_CONTOUR_COULEUR).css('background-color');
    this._vectorLayerStyle.styleAutres.strokeWidth = $(LayerProperties.SYMBOLE_AUTRES_CONTOUR_EPAISSEUR).val();
    this._vectorLayerStyle.styleAutres.radius = $(LayerProperties.SYMBOLE_AUTRES_TAILLE).val();
  }

  _appliquerNouveauStyleSymboleUnique () {
    const symbolStyle = {
      fillColor: $(LayerProperties.FILL.COLOR).css('background-color'),
      strokeColor: $(LayerProperties.BUTTON.STROKE_COLOR).css('background-color'),
      strokeWidth: $(LayerProperties.LAYER_STROKE_WIDTH).val(),
      radius: $(LayerProperties.LAYER_SYMBOL_SIZE).val(),
      groupLabel: ''
    };

    this._vectorLayerStyle.singleStyle = symbolStyle;
    this._vectorLayerStyle.styleType = StyleTypes.SINGLE;
    this._layer.styleOptions = this._vectorLayerStyle;
    this._layer.updateStyle();
    VectorLayerController.créerSymboleLégende(this._layer);
  }

  _appliquerNouveauStyleGroupes () {
    const layer = this._layer;
    this._vectorLayerStyle.styleType = $(LayerProperties.SYMBOL_STYLE_TYPE).val();
    this._vectorLayerStyle.groupValueColumn = $(LayerProperties.GROUP_VALUE_SELECTOR).val();
    layer.styleOptions = this._vectorLayerStyle;
    appliquerLabelsGroupes(layer.styleOptions.symbolStyles);

    layer.updateStyle();
    VectorLayerController.créerSymboleLégende(this._layer);
  }

  /*
   * spécifique - PROPRIÉTÉS DE LA COUCHE ---------------------------------------------------------------
   */

  async _actualiserVueProprietesStyleVectorLayer () {
    const styleUpdated = await this._actualiserProprietesStyleSymbole();
    this._afficherProprietesStyleSpecifiqueTypeSymbole(false, styleUpdated);
    this._vectorLayerAdapter.showFeatureStyleProperties(this._featureType, styleUpdated);
  }

  async _actualiserProprietesStyleSymbole () {
    const layer = await this._layer;
    const styleOptions = await layer.styleOptions;

    if (styleOptions.styleType === StyleTypes.SINGLE) {
      $(LayerProperties.SYMBOL_STYLE_TYPE).val(styleOptions.styleType);
      $(LayerProperties.SYMBOL_STYLE_GROUP_LABEL).html('');
      $(LayerProperties.SYMBOL_STYLE_GROUP_LABEL).data('group', 0);
      $(LayerProperties.FILL.COLOR).css('background-color', styleOptions.singleStyle.fillColor);
      $(LayerProperties.BUTTON.STROKE_COLOR).css('background-color', styleOptions.singleStyle.strokeColor);
      $(LayerProperties.LAYER_STROKE_WIDTH).val(styleOptions.singleStyle.strokeWidth);
      $(LayerProperties.LAYER_SYMBOL_SIZE).val(styleOptions.singleStyle.radius);
    } else {
      $(LayerProperties.SYMBOL_STYLE_TYPE).val(styleOptions.styleType);
      $(LayerProperties.GROUP_VALUE_SELECTOR).val(styleOptions.groupValueColumn);
    }

    $(LayerProperties.SYMBOLE_AUTRES_COULEUR).css('background-color', styleOptions.styleAutres.fillColor);
    $(LayerProperties.SYMBOLE_AUTRES_CONTOUR_COULEUR).css('background-color', styleOptions.styleAutres.strokeColor);
    $(LayerProperties.SYMBOLE_AUTRES_CONTOUR_EPAISSEUR).val(styleOptions.styleAutres.strokeWidth);
    $(LayerProperties.SYMBOLE_AUTRES_TAILLE).val(styleOptions.styleAutres.radius);

    this._vectorLayerAdapter.createSymbolPreview();
  }

  /** @param { boolean } reset */
  _afficherProprietesStyleSpecifiqueTypeSymbole (reset) {
    const styleType = $(LayerProperties.SYMBOL_STYLE_TYPE).val();

    fermerInfoClassification();

    if (reset) {
      this._reinitialiserLuminosite();
      reinitialiserTableGroupes();
      $(LayerProperties.SYMBOL_STYLE).show();
    }

    switch (styleType) {
      case StyleTypes.SINGLE:
        $(CLASSE_TYPE_CATÉGORISÉ).hide();
        $(CLASSE_TYPE_GRADUÉ).hide();
        $(LayerProperties.INFO_NULLES).hide();
        if (reset) this._setStyleTypeToSingle();
        this._actualiserProprietesStyleSymbole();
        break;
      case StyleTypes.CATEGORIZED:
        $(CLASSE_TYPE_GRADUÉ).hide();
        $(CLASSE_TYPE_CATÉGORISÉ).show();
        $(LayerProperties.INFO_NULLES).show();
        $('option.attribute-type--string').show();
        this._afficherSectionGroupesCategorie(reset);
        break;
      case StyleTypes.GRADUATED:
        $(LayerProperties.INFO_NULLES).show();
        this._afficherProprietesGroupesGrade(reset);
        break;
      default:
        console.log('erreur : Style Type inconnu');
    }
  }

  async _setStyleTypeToSingle () {
    const layer = await this._layer;
    const styleOptions = await layer.styleOptions;

    styleOptions.styleType = StyleTypes.SINGLE;
  }

  async _addVectorLayerColumnsToLayerProperties () {
    const _this = this;
    $(LayerProperties.GROUP_VALUE_SELECTOR).empty();
    if (Object.keys(this._layer.attributesTypes).length === 0) {
      this._layer.creerListeAttributs();
      console.log('création liste attributs');
    }
    const columns = await this._layer.olLayerColumns;
    const attributesTypes = await this._layer.attributesTypes;

    if (columns !== undefined) {
      columns.forEach(function (val, index) {
        if (index === 0) {
          const defaultOption = '<option hidden disabled selected value>' + (window.localStorage.getItem('lang') === 'en' ? dictionary.properties.selectColumn.en : dictionary.properties.selectColumn.fr) + '</option>';
          $(LayerProperties.GROUP_VALUE_SELECTOR).append(defaultOption);
        }
        const input = '<option class="attribute-type--' + attributesTypes[val] + '" value="' + val + '">' + val + '</option>';
        $(LayerProperties.GROUP_VALUE_SELECTOR).append(input);
      });
      if ($(LayerProperties.SYMBOL_STYLE_TYPE).val() !== StyleTypes.SINGLE) {
        $(LayerProperties.GROUP_VALUE_SELECTOR).val(_this._layer.styleOptions.groupValueColumn);
        $(LayerProperties.COLOR_THEME_SELECTOR).val(_this._layer.styleOptions.colorTheme);
      }
    } else {
      console.log('_addVectorLayerColumnsToLayerProperties() -> this._layer.olLayerColumns = undefined');
    }
  }

  /** @param { boolean } reset - si remet valeur par défaut */
  async _afficherSectionGroupes (reset) {
    const styleType = $(LayerProperties.SYMBOL_STYLE_TYPE).val();

    switch (styleType) {
      case StyleTypes.CATEGORIZED:
        this._afficherSectionGroupesCategorie(reset);
        break;
      case StyleTypes.GRADUATED:
        this._afficherSectionGroupesGrade(reset);
        break;
      default:
        console.log('erreur : Style Type');
    }
  }

  /*
   * spécifique - style catégorisé ---------------------------------------------------------------
   */

  /** @param { boolean } reinitialiser - si remet valeur par défaut */
  async _afficherSectionGroupesCategorie (reinitialiser) {
    const layer = await this._layer;
    const nomColonneGroupe = $(LayerProperties.GROUP_VALUE_SELECTOR).val();
    const groupes = nomColonneGroupe ? layer.obtenirValeursDistinctes(nomColonneGroupe) : undefined; // getLayerColumnData
    const nombreGroupes = groupes ? groupes.length : 0;

    reinitialiserTableGroupes(groupes);
    ajusterEtatSelecteurLuminosite(nombreGroupes);

    let symbolStyles;
    if (reinitialiser) {
      this._reinitialiserLuminosite();
      symbolStyles = await this._vectorLayerAdapter.getNewSymbolStyles(layer);
      this._vectorLayerStyle.styleType = $(LayerProperties.SYMBOL_STYLE_TYPE).val();
      this._vectorLayerStyle.symbolStyles = await symbolStyles;
    } else {
      symbolStyles = await this._layer.styleOptions.symbolStyles;
      this._vectorLayerStyle.symbolStyles = await symbolStyles;
    }
    if (nomColonneGroupe) {
      const advancedStyleObject = await symbolStyles.get(groupes[0]);
      this._vectorLayerAdapter.createGroupsPropertiesRows(await symbolStyles);
      advancedStyleObject && this._vectorLayerAdapter.updatePropertyEditorValues(advancedStyleObject);
      this._ecouterSelectionGroupeAEditer(await symbolStyles);
    }
    $(CLASSE_TYPE_GRADUÉ).hide();
    $(CLASSE_TYPE_CATÉGORISÉ).show();
  }

  _reinitialiserLuminosite () {
    this._vectorLayerStyle.colorTheme = Luminosity.BRIGHT;
    $(LayerProperties.COLOR_THEME_SELECTOR).val(Luminosity.BRIGHT);
  }

  /*
   * spécifique - style gradué ------------------------------------------------------------------
   */

  /** @param { boolean } reinitialiser - si remet valeur par défaut */
  _afficherProprietesGroupesGrade (reinitialiser) {
    const attributGroupe = $(LayerProperties.GROUP_VALUE_SELECTOR).val();
    const typeClassification = $(LayerProperties.GROUP_CLASSIFICATION_SELECTOR).val();
    $(CLASSE_TYPE_CATÉGORISÉ).hide();
    $(CLASSE_TYPE_GRADUÉ).show();
    fermerInfoClassification();
    $('option.attribute-type--string').hide();
    if (attributGroupe) {
      if (this._layer.attributesTypes[attributGroupe] === TypeAttribut.STRING) {
        $(LayerProperties.GROUP_VALUE_SELECTOR).val(undefined);
        $(LayerProperties.SYMBOL_STYLE).hide();
      }
      this._afficherSectionGroupesGrade(reinitialiser);
    }
    if (this._layer.featureType === FeatureType.POLYGON) {
      $(LayerProperties.GRADATION_TYPE).hide();
    } else {
      $(LayerProperties.GRADATION_TYPE).show();
    }
    if (typeClassification === ClassificationType.ATTRIBUTE || typeClassification === ClassificationType.STD_DEVIATION) {
      $(LayerProperties.GROUP_CLASSIFICATION_QUANTITY_ROW).hide();
    } else {
      if (attributGroupe) this._ajusterQuantiteGroupeMax();
      $(LayerProperties.GROUP_CLASSIFICATION_QUANTITY_ROW).show();
    }
  }

  async _ajusterQuantiteGroupeMax () {
    const layer = await this._layer;
    const attributeColumn = $(LayerProperties.GROUP_VALUE_SELECTOR).val();
    const attributeValues = layer.getLayerColumnData(attributeColumn);
    const valeurUniques = attributeValues.filter((v, i, a) => a.indexOf(v) === i);
    const max = valeurUniques.length;
    const maxString = '' + max;

    $(LayerProperties.GROUP_CLASSIFICATION_QUANTITY_SELECTOR).attr('max', maxString);
    if ($(LayerProperties.GROUP_CLASSIFICATION_QUANTITY_SELECTOR).val() > max) {
      $(LayerProperties.GROUP_CLASSIFICATION_QUANTITY_SELECTOR).val(max);
    }
  }

  /** @param { boolean } reset - si remet valeur par défaut */
  async _afficherSectionGroupesGrade (reset) {
    const layer = await this._layer;
    const valeurAttributs = layer.getLayerColumnData($(LayerProperties.GROUP_VALUE_SELECTOR).val());

    reinitialiserTableGroupes(valeurAttributs);

    if (valeurAttributs[0] === undefined) return;

    modifierChoixClassifications(valeurAttributs);

    const symbolStyles = reset ? await this._createNewGraduatedSymbolStyles() : await this._createExistingGraduatedSymbolStyles();

    if ($(LayerProperties.GROUP_VALUE_SELECTOR).val() && symbolStyles) {
      $(LayerProperties.SYMBOL_STYLE).show();
      this._vectorLayerAdapter.createGroupsPropertiesRows(await symbolStyles);
      this._vectorLayerAdapter.updatePropertyEditorValues(await symbolStyles.values().next().value);
      this._ecouterSelectionGroupeAEditer(await symbolStyles);
    } else {
      console.log('symbolStyles: %o', symbolStyles);
    }
  }

  /** @returns { Map < number, GroupStyle > } */
  async _createNewGraduatedSymbolStyles () {
    const typeClassification = $(LayerProperties.GROUP_CLASSIFICATION_SELECTOR).val();
    $(LayerProperties.COLOR_GRADIENT_SELECTOR).val(this._vectorLayerStyle.colorGradient);
    this._vectorLayerStyle.classification = await this._gererClassification(typeClassification);
    const symbolStyles = this._vectorLayerAdapter.obtenirNouveauxStylesSymbolesGradués(await this._vectorLayerStyle._classification);
    this._vectorLayerStyle.styleType = $(LayerProperties.SYMBOL_STYLE_TYPE).val();
    this._vectorLayerStyle.symbolStyles = await symbolStyles;

    return symbolStyles;
  }

  /** @returns { Map < number, GroupStyle > } */
  async _createExistingGraduatedSymbolStyles () {
    const layer = await this._layer;
    this._vectorLayerStyle = await layer.styleOptions;
    const symbolStyles = await layer.styleOptions.symbolStyles;
    const classification = await layer.styleOptions.classification;
    const gradationType = await this._vectorLayerStyle.gradationType;
    const gradationTypeInput = LayerProperties.GRADATION_TYPE_INPUT + '-' + gradationType;
    $(gradationTypeInput).prop('checked', true);

    if (await classification.type) {
      $(LayerProperties.GROUP_CLASSIFICATION_SELECTOR).val(classification.type);
      classification.type === ClassificationType.ATTRIBUTE ? $(LayerProperties.GROUP_CLASSIFICATION_QUANTITY_ROW).hide() : $(LayerProperties.GROUP_CLASSIFICATION_QUANTITY_ROW).show();
    }
    if (classification.groups) {
      $(LayerProperties.PRECISION_SELECTOR).val(classification.precision);
      $(LayerProperties.GROUP_CLASSIFICATION_QUANTITY_SELECTOR).val(classification.groups.ranges.length);
    }
    $(LayerProperties.COLOR_GRADIENT_SELECTOR).val(await this._vectorLayerStyle.colorGradient);

    return symbolStyles;
  }

  /**
   * @param { ClassificationType } typeClassification
   * @returns { object }
   */
  async _gererClassification (typeClassification) {
    const layer = await this._layer;
    const attributeColumn = $(LayerProperties.GROUP_VALUE_SELECTOR).val();
    const attributeValues = layer.getLayerColumnData(attributeColumn);

    if (attributeValues[0] !== undefined || attributeValues[0] !== null) {
      const attributeType = layer.attributesTypes[attributeColumn];
      const precision = gererPrécisionSelonTypeAttribut(attributeType);
      const groupQuantity = $(LayerProperties.GROUP_CLASSIFICATION_QUANTITY_SELECTOR).val();
      let groupsStat;

      switch (typeClassification) {
        case ClassificationType.ATTRIBUTE:
          $(LayerProperties.GROUP_CLASSIFICATION_QUANTITY_ROW).hide();
          console.log(attributeValues);
          groupsStat = VectorLayerStyle.obtenirClassificationParValeurAttributs(attributeValues);
          break;
        case ClassificationType.EQ_INTERVAL:
          await this._ajusterQuantiteGroupeMax();
          $(LayerProperties.GROUP_CLASSIFICATION_QUANTITY_ROW).show();
          groupsStat = VectorLayerStyle.obtenirClassificationParIntervalEgaux(attributeValues, precision, groupQuantity);
          break;
        case ClassificationType.GEOMETRIC:
          // ! cette méthode peut être incorrecte (et dupliquer 2 fois un meme groupe si les données ne sont pas adéquates).
          await this._ajusterQuantiteGroupeMax();
          $(LayerProperties.GROUP_CLASSIFICATION_QUANTITY_ROW).show();
          groupsStat = VectorLayerStyle.obtenirClassificationParProgressionGeometrique(attributeValues, precision, groupQuantity);
          break;
        case ClassificationType.JENKS:
          await this._ajusterQuantiteGroupeMax();
          $(LayerProperties.GROUP_CLASSIFICATION_QUANTITY_ROW).show();
          groupsStat = VectorLayerStyle.obtenirClassificationParJenks(attributeValues, precision, groupQuantity);
          break;
        case ClassificationType.STD_DEVIATION:
          $(LayerProperties.GROUP_CLASSIFICATION_QUANTITY_ROW).hide();
          groupsStat = VectorLayerStyle.obtenirClassificationParEcartType(attributeValues);
          break;
        case ClassificationType.QUANTILE:
          // ! non-fonctionnel, classement ne marche pas
          /* await this._ajusterQuantiteGroupeMax();
          $(LayerProperties.GROUP_CLASSIFICATION_QUANTITY_ROW).show();
          groupsStat = VectorLayerStyle.obtenirClassificationParQuantile(attributeValues, precision); */
          console.log('classification quantile non-fonctionnelle');
          break;
        default:
          console.log('erreur classification');
      }

      await groupsStat.doCount();

      return {
        type: typeClassification,
        groups: await groupsStat,
        precision: precision,
        nbGroupes: groupQuantity
      };
    } else {
      console.log(attributeValues[0]);
      return {};
    }
  }

  /** @param { ColorGradient } colorGradientValue */
  _changerGradientCouleur (colorGradientValue) {
    this._vectorLayerStyle.colorGradient = colorGradientValue;
    this._afficherSectionGroupes(true);
  }

  // ! bug ici - en cours (cacher couleur si par taille)
  _changerTypeGradation () {
    const checkedId = LayerProperties.GRADATION_TYPE_INPUT + ':checked';
    this._vectorLayerStyle.gradationType = $(checkedId).val();
    console.log(this._vectorLayerStyle.gradationType);
    this._afficherSectionGroupesGrade(true);
  }

  /** @param { Map <number,GroupStyle> } groupStyles */
  _ecouterSelectionGroupeAEditer (groupStyles) {
    const _this = this;
    $(LayerProperties.ADVANCED_PREVIEW.ROW).each(function () {
      const _row = this;
      $(this).on('click tap', function () {
        _this._vectorLayerAdapter.updatePropertyEditorValues(groupStyles.get($(_row).data('group')));
      });
    });
  }

  _actualiserSymbolesFenetreProprietes () {
    this._vectorLayerAdapter.createSymbolPreview();
    this._vectorLayerAdapter.updatePropertyEditorGroupSymbol(this._vectorLayerStyle);
  }

  _actualiserProprietesGroupesDepuisChangementClassification () {
    this._afficherSectionGroupesGrade(true);
  }

  /** @param { string } luminosity */
  _changerLuminosité (luminosity) {
    const styles = this._vectorLayerStyle.symbolStyles;
    let i = 0;

    styles.forEach(function (style, index) {
      const symbolPreviewDiv = '.symbol-preview__content--' + style.id + ' div';

      let newFillColor;
      let newStrokeColor;
      if (luminosity === Luminosity.LIGHT) {
        newFillColor = ColorHexList.LIGHT[i];
        newStrokeColor = ColorHexList.BRIGHT[i];
      } else if (luminosity === Luminosity.BRIGHT) {
        newFillColor = ColorHexList.BRIGHT[i];
        newStrokeColor = ColorHexList.DARK[i];
      } else if (luminosity === Luminosity.DARK) {
        newFillColor = ColorHexList.DARK[i];
        newStrokeColor = ColorHexList.BRIGHT[i];
      }

      $(symbolPreviewDiv).css('background-color', newFillColor);
      $(symbolPreviewDiv).css('border-color', newStrokeColor);
      style.fillColor = newFillColor;
      style.strokeColor = newStrokeColor;
      i++;
    });

    this._vectorLayerAdapter.updatePropertyEditorValues(styles.values().next().value);
    this._vectorLayerStyle.colorTheme = luminosity;
  }

  /*
   * COLOR PICKER
   */

  _showFillColorPicker () {
    const _this = this;
    const color = $(LayerProperties.FILL.COLOR).css('background-color');
    this._colorPicker.color.rgbaString = color;

    $(LayerProperties.COLOR_PICKER.CONTAINER).toggleClass('hidden');
    $(LayerProperties.COLOR_PICKER.APPLY_BUTTON).off('click tap').on('click tap', () => _this._updateLayerFillColorPropertyFromColorPicker());
  }

  _updateLayerFillColorPropertyFromColorPicker () {
    const color = this._colorPicker.color.rgbaString;
    $(LayerProperties.FILL.COLOR).css('background-color', color);
    this._actualiserSymbolesFenetreProprietes();
    $(LayerProperties.COLOR_PICKER.CONTAINER).toggleClass('hidden');
  }

  _showStrokeColorPicker () {
    const _this = this;
    const color = $(LayerProperties.BUTTON.STROKE_COLOR).css('background-color');

    this._colorPicker.color.rgbaString = color;
    $(LayerProperties.COLOR_PICKER.CONTAINER).toggleClass('hidden');
    $(LayerProperties.COLOR_PICKER.APPLY_BUTTON).off('click tap').on('click tap', () => _this._updateLayerStrokeColorPropertyFromColorPicker());
  }

  _updateLayerStrokeColorPropertyFromColorPicker () {
    const color = this._colorPicker.color.rgbaString;
    $(LayerProperties.BUTTON.STROKE_COLOR).css('background-color', color);
    this._actualiserSymbolesFenetreProprietes();
    $(LayerProperties.COLOR_PICKER.CONTAINER).toggleClass('hidden');
  }

  _afficherSelecteurCouleurRemplissageAutres () {
    const _this = this;
    const color = $(LayerProperties.SYMBOLE_AUTRES_COULEUR).css('background-color');

    this._colorPicker.color.rgbaString = color;
    $(LayerProperties.COLOR_PICKER.CONTAINER).toggleClass('hidden');
    $(LayerProperties.COLOR_PICKER.APPLY_BUTTON).off('click tap').on('click tap', () => _this._actualiserCouleurRemplissageAutresDepuisSelecteurCouleur());
  }

  _actualiserCouleurRemplissageAutresDepuisSelecteurCouleur () {
    const color = this._colorPicker.color.rgbaString;
    $(LayerProperties.SYMBOLE_AUTRES_COULEUR).css('background-color', color);
    this._actualiserSymbolesFenetreProprietes();
    $(LayerProperties.COLOR_PICKER.CONTAINER).toggleClass('hidden');
  }

  _afficherSelecteurCouleurContourAutres () {
    const _this = this;
    const color = $(LayerProperties.SYMBOLE_AUTRES_CONTOUR_COULEUR).css('background-color');

    this._colorPicker.color.rgbaString = color;
    $(LayerProperties.COLOR_PICKER.CONTAINER).toggleClass('hidden');
    $(LayerProperties.COLOR_PICKER.APPLY_BUTTON).off('click tap').on('click tap', () => _this._actualiserCouleurContourAutresDepuisSelecteurCouleur());
  }

  _actualiserCouleurContourAutresDepuisSelecteurCouleur () {
    const color = this._colorPicker.color.rgbaString;
    $(LayerProperties.SYMBOLE_AUTRES_CONTOUR_COULEUR).css('background-color', color);
    this._actualiserSymbolesFenetreProprietes();
    $(LayerProperties.COLOR_PICKER.CONTAINER).toggleClass('hidden');
  }

  /*
   * specific - LOCAL
   */

  /**
   * @param { VectorLayer } vectorLayer
   * @param { string } featureType
   */
  async _executerMethodesDependantesDeFeatureType (vectorLayer, featureType) {
    if ($('#map-layers__layer-' + vectorLayer.id).length === 0) {
      const pret = await this._ajouterCoucheDansMenuListeCouches(vectorLayer); // ! ------------------------ 2023-03-08 ordre layer bug ici
      this._vectorLayerAdapter = new VectorLayerAdapter(featureType, pret);
      VectorLayerController.créerSymboleLégende(vectorLayer, pret);
    } else {
      this._vectorLayerAdapter = new VectorLayerAdapter(featureType);
    }
  }

  /*
   * specific - LISTENERS
   */
  /** @param { VectorLayer } vectorLayer */
  _ecouterFeatureTypePret (vectorLayer) {
    const _this = this;
    vectorLayer._featureType
      ? this._executerMethodesDependantesDeFeatureType(vectorLayer, vectorLayer._featureType)
      : vectorLayer.registerFeatureTypeReadyListener(function (type) {
        _this._executerMethodesDependantesDeFeatureType(vectorLayer, type);
      });
  }
}

/**
 * @param { TypeAttribut } typeAttribut
 * @returns { number }
 */
function gererPrécisionSelonTypeAttribut (typeAttribut) {
  let precision;
  if (typeAttribut === TypeAttribut.INTEGER) {
    precision = 0;
    $(LayerProperties.PRECISION_SELECTOR).val(precision);
    $(LayerProperties.PRECISION).hide();
  } else {
    precision = $(LayerProperties.PRECISION_SELECTOR).val();
    $(LayerProperties.PRECISION).show();
  }
  return precision;
}

/** */
function afficherInfoClassification () {
  $(CLASSE_INFO_CLASSIFICATION).show();
  $('.symbol-style').hide();
  $('.info-classification-button').addClass('active').off('click tap').on('click tap', () => fermerInfoClassification());
}

/** */
function fermerInfoClassification () {
  $(CLASSE_INFO_CLASSIFICATION).hide();
  $('.symbol-style').show();
  $('.info-classification-button').removeClass('active').off('click tap').on('click tap', () => afficherInfoClassification());
}

/** */
function reinitialiserTableGroupes () {
  const tableDivContent = `
  <tr>
    <th data-text="properties.symbol"></th>
    <th data-text="properties.group"></th>
    <th data-text="properties.label"></th>
    <th class="type-graduated" data-text="properties.counter"></th>
  </tr>`;

  $(LayerProperties.ADVANCED_PREVIEW.CONTAINER).empty();
  $(LayerProperties.ADVANCED_PREVIEW.CONTAINER).append(tableDivContent);

  Evenements.texteAjoute.notify();
}

/** @returns { MutationObserver } */
function ecouterAfficherSymbolStyle () {
  const target = document.querySelector('.symbol-style');
  const observer = new window.MutationObserver(function (mutations) {
    if (target.getAttribute('style') !== 'display: none;') {
      fermerInfoClassification();
    }
  });
  observer.observe(target, {
    attributes: true
  });

  return observer;
}

/** @param { object [] } groupes */
function appliquerLabelsGroupes (groupes) {
  groupes.forEach(function (group, index) {
    const inputId = '#advanced-preview__text--' + group.id;
    group.groupLabel = $(inputId).val();
  });
}

/** @param { number } nombreGroupes */
function ajusterEtatSelecteurLuminosite (nombreGroupes) {
  nombreGroupes > 10 || nombreGroupes < 1 ? $(LayerProperties.COLOR_THEME_SELECTOR).attr('disabled', true) : $(LayerProperties.COLOR_THEME_SELECTOR).attr('disabled', false);
}

/** @param { number[] } valeurAttributs */
function modifierChoixClassifications (valeurAttributs) {
  if (Math.min(...valeurAttributs) <= 0) {
    $(LayerProperties.CLASSIFICATION_TYPE_GEOMETRIC).hide();
    if ($(LayerProperties.GROUP_CLASSIFICATION_SELECTOR).val() === ClassificationType.GEOMETRIC) {
      $(LayerProperties.GROUP_CLASSIFICATION_SELECTOR).val(ClassificationType.ATTRIBUTE);
    }
  } else {
    $(LayerProperties.CLASSIFICATION_TYPE_GEOMETRIC).show();
  }
}
